import type { SupabaseClient } from "@supabase/supabase-js";

// White-label brand profile read model — public.management_brand_profile (per-account brand
// profiles: display name, theme, colors, active/default flags; management-scoped RLS). Read-only
// preview. The view already omits raw ids beyond the account/profile id and exposes only safe
// presentation fields.

export interface BrandRow {
  brandProfileId: string;
  brandKey: string | null;
  displayName: string | null;
  themeKey: string | null;
  logoStorageKey: string | null;
  primaryColor: string | null;
  accentColor: string | null;
  active: boolean;
  isDefault: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

export function mapBrandRow(raw: Record<string, unknown>): BrandRow {
  return {
    brandProfileId: asString(raw.brand_profile_id) ?? "",
    brandKey: asString(raw.brand_key),
    displayName: asString(raw.display_name),
    themeKey: asString(raw.theme_key),
    logoStorageKey: asString(raw.logo_storage_key),
    primaryColor: asString(raw.primary_color),
    accentColor: asString(raw.accent_color),
    active: raw.active === true,
    isDefault: raw.is_default === true,
    createdAt: asString(raw.created_at),
    updatedAt: asString(raw.updated_at),
  };
}

const BRAND_LIMIT = 50;

/** Fetch the account's brand profiles, most recently updated first. Throws on read error. */
export async function fetchBrandProfiles(
  client: SupabaseClient,
  accountId: string,
  limit: number = BRAND_LIMIT,
): Promise<BrandRow[]> {
  const { data, error } = await client
    .from("management_brand_profile")
    .select("*")
    .eq("account_id", accountId)
    .order("updated_at", { ascending: false })
    .limit(limit);
  if (error !== null) {
    throw new Error("Could not load brand profiles.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((row) => mapBrandRow(row as Record<string, unknown>));
}
